import { FunctionComponent, useState } from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import PasswordInput from 'components/forms/passwordInput'

import * as userApi from 'features/user/api.client'
import { AppMessage } from 'utilities/messages'
import { getApiErrorMessage } from '../apiError'
import AccountSection from './accountSection'
import accountCss from '../account.module.scss'

const PASSWORD_MIN_LENGTH = 8

const EMPTY = { currentPassword: '', password: '', passwordConfirmation: '' }

const PasswordSection: FunctionComponent = () => {
  const [values, setValues] = useState(EMPTY)
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<AppMessage>({ show: false })

  const tooShort = values.password.length < PASSWORD_MIN_LENGTH
  const mismatch = values.password !== values.passwordConfirmation
  const missingCurrent = values.currentPassword.length === 0

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setValues({ ...values, [name]: value })
  }

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)

    if (missingCurrent || tooShort || mismatch) {
      return
    }

    setSaving(true)
    setMessage({ show: false })

    try {
      await userApi.changeMyPassword(values)
      setValues(EMPTY)
      setSubmitted(false)
      setMessage({ show: true, success: true, message: 'Senha alterada.' })
    } catch (error) {
      setMessage({
        show: true,
        success: false,
        message: getApiErrorMessage(error, 'Erro ao alterar a senha.'),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <AccountSection icon="lock" title="Alterar senha">
      {message.show && (
        <Alert
          variant={message.success ? 'success' : 'danger'}
          onClose={() => setMessage({ show: false })}
          dismissible
        >
          {message.message}
        </Alert>
      )}

      <Form noValidate onSubmit={save}>
        <div className="form-grid form-grid-2">
          <Form.Group className="span-2" controlId="account-current-password">
            <Form.Label>Senha atual</Form.Label>
            <PasswordInput
              name="currentPassword"
              autoComplete="current-password"
              wrapperClassName={accountCss.halfWidth}
              value={values.currentPassword}
              isInvalid={submitted && missingCurrent}
              onChange={handleChange}
            >
              <Form.Control.Feedback type="invalid">
                Informe a senha atual.
              </Form.Control.Feedback>
            </PasswordInput>
          </Form.Group>

          <Form.Group controlId="account-new-password">
            <Form.Label>Nova senha</Form.Label>
            <PasswordInput
              name="password"
              autoComplete="new-password"
              value={values.password}
              isInvalid={submitted && tooShort}
              onChange={handleChange}
            >
              <Form.Control.Feedback type="invalid">
                A senha deve conter no mínimo 8 caracteres.
              </Form.Control.Feedback>
            </PasswordInput>
          </Form.Group>

          <Form.Group controlId="account-confirm-password">
            <Form.Label>Confirmar nova senha</Form.Label>
            <PasswordInput
              name="passwordConfirmation"
              autoComplete="new-password"
              value={values.passwordConfirmation}
              isInvalid={submitted && mismatch}
              onChange={handleChange}
            >
              <Form.Control.Feedback type="invalid">
                As senhas não conferem.
              </Form.Control.Feedback>
            </PasswordInput>
          </Form.Group>
        </div>

        <div className={accountCss.actions}>
          <Button type="submit" variant="secondary" disabled={saving}>
            {saving && <Spinner size="sm" animation="border" />}
            Alterar senha
          </Button>
        </div>
      </Form>
    </AccountSection>
  )
}

export default PasswordSection
